import { Controller } from "@hotwired/stimulus"

// Lets a vendor drag their cover photo inside the banner to choose which part
// shows. The framing is stored as CSS object-position percentages, so it holds
// for every crop of the cover (wide banner, 4:3 search cards).
export default class extends Controller {
  static targets = ["surface", "image", "idle", "editing", "x", "y"]

  connect() {
    this.position = [Number(this.xTarget.value), Number(this.yTarget.value)]
  }

  start() {
    this.savedPosition = [...this.position]
    this.idleTarget.hidden = true
    this.editingTarget.hidden = false
    this.surfaceTarget.tabIndex = 0
    this.surfaceTarget.classList.add("cursor-grab", "ring-4", "ring-inset", "ring-primary/60")
    this.surfaceTarget.focus()
  }

  cancel() {
    this.position = this.savedPosition
    this.render()
    this.stop()
  }

  // turbo:submit-end from the Save form
  saved(event) {
    if (event.detail.success) this.stop()
  }

  stop() {
    this.editingTarget.hidden = true
    this.idleTarget.hidden = false
    this.surfaceTarget.removeAttribute("tabindex")
    this.surfaceTarget.classList.remove("cursor-grab", "cursor-grabbing", "ring-4", "ring-inset", "ring-primary/60")
    this.idleTarget.querySelector("button")?.focus()
  }

  grab(event) {
    if (!this.editing) return
    event.preventDefault()
    this.surfaceTarget.setPointerCapture(event.pointerId)
    this.surfaceTarget.classList.replace("cursor-grab", "cursor-grabbing")
    this.dragStart = { x: event.clientX, y: event.clientY, position: [...this.position] }
  }

  drag(event) {
    if (!this.dragStart) return
    const [overflowX, overflowY] = this.overflow()
    const [startX, startY] = this.dragStart.position
    // Dragging the photo down reveals more of its top, i.e. a smaller y%.
    const x = overflowX > 0 ? startX - ((event.clientX - this.dragStart.x) / overflowX) * 100 : startX
    const y = overflowY > 0 ? startY - ((event.clientY - this.dragStart.y) / overflowY) * 100 : startY
    this.position = [x, y].map(clamp)
    this.render()
  }

  release() {
    if (!this.dragStart) return
    this.dragStart = null
    this.surfaceTarget.classList.replace("cursor-grabbing", "cursor-grab")
  }

  nudge(event) {
    if (!this.editing) return
    const step = event.shiftKey ? 20 : 5
    const moves = { ArrowUp: [0, step], ArrowDown: [0, -step], ArrowLeft: [step, 0], ArrowRight: [-step, 0] }
    if (event.key === "Escape") return this.cancel()
    if (event.key === "Enter") return this.editingTarget.querySelector("form").requestSubmit()
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    this.position = [this.position[0] + move[0], this.position[1] + move[1]].map(clamp)
    this.render()
  }

  render() {
    const [x, y] = this.position.map(Math.round)
    this.imageTarget.style.objectPosition = `${x}% ${y}%`
    this.xTarget.value = x
    this.yTarget.value = y
  }

  // How far the scaled image overhangs the banner on each axis — the distance
  // a drag from 0% to 100% covers.
  overflow() {
    const img = this.imageTarget
    const { clientWidth: width, clientHeight: height, naturalWidth, naturalHeight } = img
    if (!naturalWidth || !naturalHeight) return [0, 0]
    const scale = Math.max(width / naturalWidth, height / naturalHeight)
    return [naturalWidth * scale - width, naturalHeight * scale - height]
  }

  get editing() {
    return !this.editingTarget.hidden
  }
}

const clamp = (value) => Math.min(100, Math.max(0, value))
